import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ITask } from '@/types/task';
import { TASK_STATUS_COLORS, TASK_STATUS_LABELS } from '@/types/project';

interface ActivityLogProps {
  tasks: ITask[];
}

const INITIAL_SHOW = 10;

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffSec = Math.floor((now - then) / 1000);

  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 172800) return '1d ago';
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function dateGroup(dateStr: string, todayLabel: string, yesterdayLabel: string, earlierLabel: string): string {
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const taskDay = new Date(d);
  taskDay.setHours(0, 0, 0, 0);

  if (taskDay.getTime() === today.getTime()) return todayLabel;
  if (taskDay.getTime() === yesterday.getTime()) return yesterdayLabel;
  return earlierLabel;
}

function getInitial(name: string): string {
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
}

const AVATAR_COLORS = [
  'bg-emerald-500',
  'bg-blue-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-teal-500',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const ActivityLog: React.FC<ActivityLogProps> = ({ tasks }) => {
  const { t } = useTranslation('project');
  const [showCount, setShowCount] = useState(INITIAL_SHOW);
  const [collapsed, setCollapsed] = useState(false);

  const sortedTasks = useMemo(
    () => [...tasks].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
    [tasks]
  );

  const visibleTasks = sortedTasks.slice(0, showCount);

  // Group tasks by date
  const grouped = useMemo(() => {
    const groups: { label: string; tasks: typeof visibleTasks }[] = [];
    let currentLabel = '';
    visibleTasks.forEach((task) => {
      const label = dateGroup(
        task.updated_at,
        t('activity.today'),
        t('activity.yesterday'),
        t('activity.earlier')
      );
      if (label !== currentLabel) {
        groups.push({ label, tasks: [] });
        currentLabel = label;
      }
      groups[groups.length - 1].tasks.push(task);
    });
    return groups;
  }, [visibleTasks, t]);

  if (tasks.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          {t('activity.title')}
        </h3>
        <p className="text-sm text-gray-400 dark:text-gray-500">{t('activity.noActivity')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setCollapsed((prev) => !prev)}
        className="w-full px-4 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
      >
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {t('activity.title')}
        </h3>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${collapsed ? '' : 'rotate-180'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {!collapsed && (
        <div className="p-4 space-y-4">
          {grouped.map((group) => (
            <div key={group.label}>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                {group.label}
              </p>
              <div className="space-y-2">
                {group.tasks.map((task) => {
                  const owner = task.task_owner || task.created_by || '';
                  const statusKey = task.status as keyof typeof TASK_STATUS_COLORS;
                  return (
                    <div
                      key={task.id + task.updated_at}
                      className="flex items-center gap-3 py-1.5"
                    >
                      {/* Avatar */}
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${avatarColor(owner)}`}
                      >
                        {getInitial(owner)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-white truncate">
                          {task.task_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {owner}
                        </p>
                      </div>

                      {/* Status badge */}
                      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${TASK_STATUS_COLORS[statusKey] || ''}`}>
                        {TASK_STATUS_LABELS[statusKey] || task.status}
                      </span>

                      {/* Relative time */}
                      <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                        {relativeTime(task.updated_at)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Show more */}
          {showCount < sortedTasks.length && (
            <button
              onClick={() => setShowCount((prev) => prev + 10)}
              className="w-full text-center text-xs text-emerald-600 dark:text-emerald-400 hover:underline py-1"
            >
              {t('activity.showMore')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
